// Offline Mode — shared IndexedDB primitives for Slices 2 & 3.
//
//   • `route_packs`  — pre-downloaded A → B corridor packs (route polyline
//                      + POIs + user metadata) so the trip works offline.
//   • `action_queue` — queued user actions taken while offline. Each entry
//                      is drained on reconnect; failures re-queue with a
//                      capped retry counter.
//
// We deliberately keep this dependency-free (no idb-keyval / dexie) so the
// build stays lean. All functions are safe to call in SSR contexts — they
// throw a friendly "no-idb" error the caller can swallow.

const DB_NAME       = "fn_offline_v2";
const DB_VERSION    = 1;
const STORE_PACKS   = "route_packs";
const STORE_QUEUE   = "action_queue";
const MAX_PACKS     = 20;
const MAX_QUEUE     = 500;
const DEFAULT_SIZE_CAP_MB = 200;

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") return reject(new Error("no-idb"));
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_PACKS)) {
        const s = db.createObjectStore(STORE_PACKS, { keyPath: "pack_id" });
        s.createIndex("saved_at", "saved_at");
      }
      if (!db.objectStoreNames.contains(STORE_QUEUE)) {
        const s = db.createObjectStore(STORE_QUEUE, { keyPath: "id" });
        s.createIndex("created_at", "created_at");
        s.createIndex("status",     "status");
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

async function withStore(name, mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(name, mode);
    const store = tx.objectStore(name);
    let result;
    Promise.resolve(fn(store)).then(v => { result = v; }, reject);
    tx.oncomplete = () => resolve(result);
    tx.onerror    = () => reject(tx.error);
    tx.onabort    = () => reject(tx.error);
  });
}

// ─── Route packs ─────────────────────────────────────────────────────────

export async function saveRoutePack(pack) {
  if (!pack || !pack.pack_id) throw new Error("pack.pack_id required");
  const row = {
    saved_at: Date.now(),
    ...pack,
  };
  await withStore(STORE_PACKS, "readwrite", (s) => s.put(row));
  // LRU-trim: keep the newest MAX_PACKS.
  const all = await listRoutePacks();
  if (all.length > MAX_PACKS) {
    const excess = all.slice(MAX_PACKS);
    await Promise.all(excess.map(p => deleteRoutePack(p.pack_id)));
  }
  return row;
}

export async function listRoutePacks() {
  try {
    return await withStore(STORE_PACKS, "readonly", (s) => new Promise((res, rej) => {
      const out = [];
      const cur = s.index("saved_at").openCursor(null, "prev");
      cur.onsuccess = () => {
        const c = cur.result;
        if (!c) return res(out);
        out.push(c.value);
        c.continue();
      };
      cur.onerror = () => rej(cur.error);
    }));
  } catch { return []; }
}

export async function getRoutePack(packId) {
  try {
    return await withStore(STORE_PACKS, "readonly", (s) => new Promise((res, rej) => {
      const r = s.get(packId);
      r.onsuccess = () => res(r.result || null);
      r.onerror   = () => rej(r.error);
    }));
  } catch { return null; }
}

export async function deleteRoutePack(packId) {
  await withStore(STORE_PACKS, "readwrite", (s) => s.delete(packId));
}

export async function estimateOfflineStorage() {
  const packs = await listRoutePacks();
  const bytes = packs.reduce((acc, p) => acc + (p.size_estimate || JSON.stringify(p).length), 0);
  return { count: packs.length, bytes, cap_mb: DEFAULT_SIZE_CAP_MB };
}

// ─── Action queue ────────────────────────────────────────────────────────

export async function enqueueAction(action) {
  const row = {
    id:         action.id || `q_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    endpoint:   action.endpoint,
    method:     (action.method || "POST").toUpperCase(),
    payload:    action.payload ?? null,
    label:      action.label || action.endpoint,
    kind:       action.kind || "generic",
    created_at: Date.now(),
    status:     "pending",
    retries:    0,
    last_error: null,
  };
  await withStore(STORE_QUEUE, "readwrite", (s) => s.put(row));
  return row;
}

export async function listQueuedActions(status = null) {
  try {
    return await withStore(STORE_QUEUE, "readonly", (s) => new Promise((res, rej) => {
      const out = [];
      const cur = s.openCursor();
      cur.onsuccess = () => {
        const c = cur.result;
        if (!c) return res(out.sort((a, b) => a.created_at - b.created_at));
        if (!status || c.value.status === status) out.push(c.value);
        c.continue();
      };
      cur.onerror = () => rej(cur.error);
    }));
  } catch { return []; }
}

export async function updateQueuedAction(id, patch) {
  await withStore(STORE_QUEUE, "readwrite", (s) => new Promise((res, rej) => {
    const g = s.get(id);
    g.onsuccess = () => {
      const cur = g.result;
      if (!cur) return res(null);
      const next = { ...cur, ...patch };
      s.put(next).onsuccess = () => res(next);
    };
    g.onerror = () => rej(g.error);
  }));
}

export async function deleteQueuedAction(id) {
  await withStore(STORE_QUEUE, "readwrite", (s) => s.delete(id));
}

export async function pendingCount() {
  const arr = await listQueuedActions("pending");
  return arr.length;
}

// Drain — replay every pending action against the given axios `client`.
// Called by the SyncManager when connectivity returns. Idempotent; caller
// can invoke repeatedly.
export async function drainQueue(client) {
  const pending = await listQueuedActions("pending");
  const results = { drained: 0, failed: 0, deferred: 0 };
  for (const q of pending) {
    if (q.retries >= 5) {
      await updateQueuedAction(q.id, { status: "failed" });
      results.failed++;
      continue;
    }
    try {
      const cfg = { method: q.method, url: q.endpoint, data: q.payload };
      await client(cfg);
      await deleteQueuedAction(q.id);
      results.drained++;
    } catch (err) {
      await updateQueuedAction(q.id, {
        retries:    q.retries + 1,
        last_error: err?.response?.status
          ? `HTTP ${err.response.status}`
          : (err?.message || "network"),
      });
      results.deferred++;
    }
  }
  // LRU-trim any completed rows past cap.
  const all = await listQueuedActions();
  if (all.length > MAX_QUEUE) {
    const excess = all.slice(0, all.length - MAX_QUEUE);
    await Promise.all(excess.map(a => deleteQueuedAction(a.id)));
  }
  return results;
}
